import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, Gift, Mail, User } from 'lucide-react';

export const leadMagnetDelivery: Playbook = {
  meta: {
    id: 'lead-magnet-delivery',
    slug: 'lead-magnet-delivery',
    categoryId: 'amplify-marketing',
    name: 'The Lead Magnet Delivery',
    tagline: 'Deliver ebooks conversationally.',
    description: 'Distribute valuable content like ebooks, whitepapers, and guides through engaging conversation rather than static forms.',
    strategy: 'Distribute valuable content like ebooks, whitepapers, and guides through engaging conversation rather than static forms.',
    benefits: [
      'Higher content conversion rates',
      'Better lead qualification',
      'Engaging content delivery',
      'Improved user experience'
    ],
    targetAudience: ['B2B marketers', 'Content creators', 'Educational services'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'beginner',
    tags: ['lead-magnets', 'content-marketing', 'ebooks', 'lead-generation'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Lead Magnet Delivery Playbook - Conversational Content Marketing',
    metaDescription: 'Boost lead generation with conversational content delivery. Turn static forms into engaging Genie interactions.',
    keywords: ['lead magnet', 'content delivery', 'ebook marketing', 'lead generation', 'conversational marketing']
  },
  demoConfig: {
    triggerType: 'button',
    message: "📖 I have exclusive resources that can help you succeed. Which topic interests you most: Marketing automation, Sales strategies, or Customer retention?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "📖 I have 3 exclusive guides. Which topic interests you most?" },
    { speaker: 'user', message: "Marketing automation sounds good" },
    { speaker: 'agent', message: "Great choice! Our automation guide has helped 1000+ marketers save 10 hours/week." },
    { speaker: 'user', message: "I'd like to read it" },
    { speaker: 'agent', message: "Perfect! What's your email? I'll send it along with our automation checklist." }
  ],
  templateConfig: {
    firstMessage: "📖 I have valuable resources to share. What challenge are you trying to solve?",
    systemPrompt: "You are a content delivery Genie. Match visitors with relevant resources, highlight value, and collect contact information for delivery. Be enthusiastic about the content's value.",
    tools: ['resource_library', 'email_capture', 'content_delivery']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Visitor seeks information',
      description: 'User shows interest in learning about specific topic or solving business problem'
    },
    {
      type: 'genie',
      icon: Gift,
      genieIcon: 'genie-lamp',
      label: 'Offers valuable resources',
      description: 'Genie presents relevant content options based on user interests or behavior'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Qualifies content interest',
      description: 'Asks questions to match user with most relevant ebook or resource'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'settings',
      label: 'Highlights content value',
      description: 'Explains specific benefits and outcomes user will get from the resource'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures contact for delivery',
      description: 'Collects email address and delivers content immediately with follow-up sequence'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Qualified lead generated',
      description: 'User receives valuable content and enters nurturing sequence as qualified lead'
    }
  ],
  metrics: {
    conversionRate: '34%',
    responseTime: '<1 second',
    satisfactionScore: '4.6/5',
    costSavings: '$25k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'ContentPro Marketing',
      authorName: 'Jennifer Lee',
      authorTitle: 'Content Marketing Manager',
      quote: 'Conversational content delivery increased our ebook download rate by 52% and lead quality improved dramatically.',
      metrics: '52% increase in content downloads'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Content Delivery Scripts',
      description: 'Proven conversation templates for different types of lead magnets',
      url: '/templates/content-delivery'
    },
    {
      type: 'guide',
      title: 'Lead Magnet Strategy',
      description: 'How to create and promote high-converting lead magnets',
      url: '/docs/lead-magnet-strategy'
    }
  ]
};